import { extractFirstHttpUrl } from '@shared/linkedin-media-rules';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import {
  type LinkedInLinkPreview,
  linkedInLinkPreviewService,
} from '@/services/data/LinkedInLinkPreviewService';

export type { LinkedInLinkPreview };

/**
 * The article card for the first link in a LinkedIn caption, read through the same `fetchArticlePreview` the
 * publisher uses. Off unless `enabled` (a LinkedIn post with no media of its own, as on LinkedIn).
 */
export function useLinkedInLinkPreview(caption: string | null | undefined, enabled: boolean) {
  const { user, loading: authLoading } = useAuth();
  const url = enabled ? extractFirstHttpUrl(caption) : null;

  const query = useQuery({
    queryKey: ['linkedin-link-preview', url],
    queryFn: async () => {
      const response = await linkedInLinkPreviewService.fetchPreview(url as string);
      if (response.error) throw new Error(response.error.message);
      return response.data ?? null;
    },
    enabled: !!url && !!user?.id && !authLoading,
    staleTime: 10 * 60_000,
    retry: false,
  });

  const preview = query.data ?? null;
  let card: { title?: string; domain: string; imageUrl: string | null } | null = null;
  if (url) {
    try {
      card = {
        title: preview?.title ?? undefined,
        domain: preview?.domain ?? new URL(url).hostname.replace(/^www\./, ''),
        imageUrl: preview?.imageUrl ?? null,
      };
    } catch {
      card = null;
    }
  }

  return { url, preview, card, isLoading: query.isPending && !!url };
}
